// Third-party
import React from 'react';
import { connect } from 'react-redux';

// Components
import { QuestionnaireViewContainer } from '../questionnaire';
import { OTHER_QUESTION_ITEM, QUESTIONNAIRE_TYPES } from '@/utils/questionnaire';
import Modal from '@/views/components/modals/AccessibleModal';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import {
  questionnaireIsCompleted,
  getUnansweredQuestions,
  completeQuestionnaire,
  getSectionSubtotal,
  goBackQuestionnaire,
  saveAnswers,
  clearAnswers,
  getSavedSections,
  setSavedSections,
  deleteQuestionnaireAnswers,
  saveNotes,
  clearNotes,
} from '../../../redux/modules/questionnaire';

// Utils
import { PERMISSIONS } from '../../../utils/permissions';
import QuestionnaireAnswersView from '../questionnaire/QuestionnaireAnswersView';
import Questionnaire from '@/types/questionnaire';
import Nullable from '@/types/nullable';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionItem from '@/types/question-item';
import QuestionOptionItem from '@/types/question-option-item';
import SavedSections from '@/types/saved-sections';
import GenericContact from '@/types/generic-contact';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import SavedQuestions from '@/types/saved-questions';
import QuestionnaireAnswerItem from '@/types/questionnaire-answer-item';
import User from '@/types/user';

type StateProps = {
  questionnaire: Questionnaire;
  user?: Nullable<User>;
  title?: string;
};

type DispatchProps = {
  questionnaireIsCompleted: (reducerName: QUESTIONNAIRE_TYPES) => boolean;
  getUnansweredQuestions: (reducerName: QUESTIONNAIRE_TYPES, section: QuestionnaireSectionItem) => QuestionItem[];
  completeQuestionnaire: (reducerName: QUESTIONNAIRE_TYPES) => void;
  getSectionSubtotal: (reducerName: QUESTIONNAIRE_TYPES, section: QuestionnaireSectionItem) => number;
  goBackQuestionnaire: (reducerName: QUESTIONNAIRE_TYPES) => void;
  saveAnswers: (
    reducerName: QUESTIONNAIRE_TYPES,
    sectionId: string,
    questionId: string,
    option: QuestionOptionItem,
    text: string | undefined,
    notes: string | undefined,
  ) => void;
  getSavedSections: (reducerName: QUESTIONNAIRE_TYPES) => SavedSections;
  clearAnswers: (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string) => void;
  setSavedSections: (reducerName: QUESTIONNAIRE_TYPES, savedSections: SavedSections) => void;
  deleteQuestionnaireAnswers: (reducerName: QUESTIONNAIRE_TYPES, questionnaireAnswerId: string) => void;
  saveNotes: (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string, notes: string) => void;
  clearNotes: (reducerName: QUESTIONNAIRE_TYPES, sectionId: string, questionId: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    isModal: boolean;
    show?: boolean;
    current?: {
      contact: GenericContact;
    };
    saveLabel?: string;
    onClose?: () => void;
    onSave: (answers: QuestionnaireAnswers) => void;
    onEdit: (answers: QuestionnaireAnswers, questionnaire: Questionnaire) => void;
    onDelete: () => void;
    questionnaireAnswers?: QuestionnaireAnswers;
    readOnly?: boolean;
  };

type State = {
  isCompleted: boolean;
  savedSections: SavedSections;
  edit: boolean;
};

class CreateQuestionnaire extends React.Component<Props> {
  private scrollContainer?: HTMLElement;

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.questionnaire !== this.props.questionnaire || prevProps.questionnaireAnswers !== this.props.questionnaireAnswers) {
      this.setState({
        edit: false,
        savedSections: {},
      });
    }
  }

  static defaultProps = {
    show: false,
    onClose: () => {},
    readonly: false,
  };

  getIsCompleted = () => {
    const { type } = this.props.questionnaire;
    return this.props.questionnaireIsCompleted(type);
  };

  readonly state: State = {
    isCompleted: this.getIsCompleted(),
    savedSections: {},
    edit: false,
  };

  getPermissionForType = () => {
    const { questionnaire } = this.props;

    switch (questionnaire.type) {
      case QUESTIONNAIRE_TYPES.RATIONALE:
        return PERMISSIONS.RATIONALE_QUESTIONNAIRE;
      default:
        return undefined;
    }
  };

  getSortedSections = () => {
    const sections = this.props.questionnaire.sections.slice();
    return sections.sort((a, b) => a.order - b.order);
  };

  sectionIsDisabled = (section: QuestionnaireSectionItem) => {
    const { type } = this.props.questionnaire;
    const sections = this.getSortedSections();
    const previousSections = sections.slice(
      0,
      sections.findIndex(s => s._id === section._id),
    );

    return !previousSections.every(s => this.props.getUnansweredQuestions(type, s).length === 0);
  };

  setQuestionnaireComplete = () => {
    const { type } = this.props.questionnaire;
    this.props.completeQuestionnaire(type);
    this.setState({ isCompleted: true });
    this.onSave();
  };

  goBackQuestionnaire = () => {
    const { type } = this.props.questionnaire;
    this.props.goBackQuestionnaire(type);
    this.setState({ isCompleted: false });
  };

  getUnansweredQuestions = (section: QuestionnaireSectionItem) => this.props.getUnansweredQuestions(this.props.questionnaire.type, section);

  getSectionSubtotal = (section: QuestionnaireSectionItem) => this.props.getSectionSubtotal(this.props.questionnaire.type, section);

  saveAnswers = (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string) => {
    const { type } = this.props.questionnaire;
    this.props.saveAnswers(type, sectionId, questionId, option, text || '', undefined);
    this.setNewSavedSections(type);
  };

  clearAnswers = (sectionId: string, questionId: string) => {
    const { type } = this.props.questionnaire;
    this.props.clearAnswers(type, sectionId, questionId);
    this.setNewSavedSections(type);
  };

  saveNotes = (sectionId: string, questionId: string, notes?: string) => {
    const { type } = this.props.questionnaire;
    this.props.saveNotes(type, sectionId, questionId, notes || '');
    this.setNewSavedSections(type);
  };

  clearNotes = (sectionId: string, questionId: string) => {
    const { type } = this.props.questionnaire;
    this.props.clearNotes(type, sectionId, questionId);
    this.setNewSavedSections(type);
  };

  setNewSavedSections = (type: QUESTIONNAIRE_TYPES) => {
    const newSavedSections = this.props.getSavedSections(type);
    if (this.state.savedSections !== newSavedSections) {
      this.setState({ savedSections: newSavedSections });
    }
  };

  onClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  onModalExited = () => {
    this.setState({ edit: false, savedSections: {}, isCompleted: false });
  };

  answersToSavedSections = () => {
    const { questionnaireAnswers, questionnaire } = this.props;
    const savedSections: SavedSections = {};
    let isCompleted = false;

    if (questionnaireAnswers) {
      const fillQuestionnaire =
        questionnaire.updatedAt !== undefined &&
        questionnaireAnswers.updatedAt !== undefined &&
        Date.parse(questionnaire.updatedAt) <= Date.parse(questionnaireAnswers.updatedAt);

      if (fillQuestionnaire && questionnaireAnswers !== undefined) {
        questionnaire.sections.forEach(section => {
          const questions: SavedQuestions = {};
          section.questions.forEach(question => {
            const answer = questionnaireAnswers.answers.find(a => a.question._id === question._id);
            if (answer) {
              questions[question._id] = {
                // no multiple answers for none-kyc questionnaire, simply add the value to an array.
                options: answer.options ? answer.options.map(option => option._id) : [], // ? [answer.option._id] : [OTHER_QUESTION_ITEM._id],
                text: answer.text,
                notes: answer.notes,
                value: 1, // answer.options ? answer.options[0].value : 1,
              };
            }
          });
          savedSections[section._id] = { questions };
        });
        isCompleted = true;
      }
    }
    return {
      isCompleted,
      savedSections,
    };
  };

  onSave = () => {
    const { questionnaire } = this.props;
    const { savedSections } = this.state;

    const answers: QuestionnaireAnswerItem[] = [];
    questionnaire.sections.forEach(section => {
      section.questions.forEach(question => {
        const questionData = savedSections[section._id] ? savedSections[section._id].questions[question._id] : null;

        const options = questionData?.options.reduce((acc, option) => {
          if (option !== OTHER_QUESTION_ITEM._id) {
            const opt = question.options.find(o => o._id === option);
            if (opt != null) {
              acc.push(opt);
            }
          }
          return acc;
        }, [] as QuestionOptionItem[]);

        const answer: QuestionnaireAnswerItem = {
          question,
          options: options?.length ? options : undefined,
          text: questionData?.text || '',
          notes: questionData?.notes || '',
        };
        answers.push(answer);
      });
    });

    const questionnaireAnswers: QuestionnaireAnswers = { answers };
    if (this.props.questionnaireAnswers) {
      this.props.onEdit(questionnaireAnswers, questionnaire);
    } else {
      this.props.onSave(questionnaireAnswers);
    }
  };

  onEdit = () => {
    const { isCompleted, savedSections } = this.answersToSavedSections();
    this.props.setSavedSections(this.props.questionnaire.type, savedSections);
    this.setState({ edit: true, savedSections, isCompleted });
  };

  onDelete = () => {
    if (this.props.onDelete) {
      this.props.onDelete();
    }
  };

  getQuestionnaireAnswersView = () => {
    const { questionnaireAnswers, questionnaire } = this.props;
    const { edit } = this.state;

    if (questionnaireAnswers === undefined || edit) {
      return;
    }

    return <QuestionnaireAnswersView answers={questionnaireAnswers.answers} questionnaire={questionnaire} />;
  };

  getQuestionnaireProps = () => {
    const { current, questionnaire, user, saveLabel, title, questionnaireAnswers, readOnly } = this.props;
    const { edit } = this.state;
    const isAnswersDisplay = questionnaireAnswers !== undefined && !edit && !readOnly;

    return {
      title,
      isCompleted: this.state.isCompleted && !readOnly,
      userPermissionType: this.getPermissionForType(),
      questionnaire,
      user,
      current,
      onSave: this.onSave,
      onClose: this.onClose,
      onEdit: this.onEdit,
      onDelete: this.onDelete,
      displayEditDelete: isAnswersDisplay,
      saveLabel,
      sectionIsDisabled: this.sectionIsDisabled,
      setQuestionnaireComplete: this.setQuestionnaireComplete,
      getUnansweredQuestions: this.getUnansweredQuestions,
      getSectionSubtotal: this.getSectionSubtotal,
      goBackQuestionnaire: this.goBackQuestionnaire,
      saveAnswers: this.saveAnswers,
      clearAnswers: this.clearAnswers,
      saveNotes: this.saveNotes,
      clearNotes: this.clearNotes,
      savedSections: this.state.savedSections,
      customQuestionnaireView: this.getQuestionnaireAnswersView(),
    };
  };

  render() {
    const { isModal, show, questionnaire } = this.props;

    if (questionnaire === undefined) {
      return <></>;
    }

    return isModal ? (
      <Modal
        show={show}
        backdropClassName="modal-v2-backdrop"
        className="risk-profile-modal"
        onHide={this.props.onClose}
        dialogClassName="modal-dialog-centered"
        backdrop="static"
        onExited={this.onModalExited}
      >
        <Modal.Body className="d-flex flex-column">
          <QuestionnaireViewContainer {...this.getQuestionnaireProps()} />
        </Modal.Body>
      </Modal>
    ) : (
      <QuestionnaireViewContainer {...this.getQuestionnaireProps()} />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  questionnaireIsCompleted,
  getUnansweredQuestions,
  completeQuestionnaire,
  getSectionSubtotal,
  goBackQuestionnaire,
  saveAnswers,
  getSavedSections,
  clearAnswers,
  setSavedSections,
  deleteQuestionnaireAnswers,
  saveNotes,
  clearNotes,
})(CreateQuestionnaire);
